import React from "react";
import { ProjectDeveloper, Country } from "@/types";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import * as countries from "i18n-iso-countries";

// Register the languages we need
import en from "i18n-iso-countries/langs/en.json";
import de from "i18n-iso-countries/langs/de.json";

countries.registerLocale(en);
countries.registerLocale(de);

interface ProjectFilterProps {
  projectDevelopers: ProjectDeveloper[];
  selectedDeveloperId?: string;
  onDeveloperChange: (developerId: string | undefined) => void;
  isLoading: boolean;
  countries: Country[];
  selectedCountryCode?: string;
  onCountryChange: (countryCode: string | undefined) => void;
  isLoadingCountries: boolean;
}

const ProjectFilter: React.FC<ProjectFilterProps> = ({
  projectDevelopers,
  selectedDeveloperId,
  onDeveloperChange,
  isLoading,
  countries: countryList,
  selectedCountryCode,
  onCountryChange,
  isLoadingCountries,
}) => {
  const { t, language } = useLanguage();

  const handleDeveloperChange = (value: string) => {
    if (value === "all") {
      onDeveloperChange(undefined);
    } else {
      onDeveloperChange(value);
    }
  };

  const handleCountryChange = (value: string) => {
    if (value === "all") {
      onCountryChange(undefined);
    } else {
      onCountryChange(value);
    }
  };

  const developerId = "developer-select";
  const countryId = "country-select";

  // Get localized country name based on current language
  const getLocalizedCountryName = (countryCode: string) => {
    const localizedName = countries.getName(
      countryCode,
      language === "de" ? "de" : "en"
    );
    return localizedName || countryCode;
  };

  const renderCountryOption = (countryCode: string) => (
    <div className="flex items-center gap-2">
      <div className="text-xl" title={countryCode.toUpperCase()}>
        {String.fromCodePoint(
          ...countryCode
            .toUpperCase()
            .split("")
            .map((char) => 127397 + char.charCodeAt(0))
        )}
      </div>
      <span>{getLocalizedCountryName(countryCode)}</span>
    </div>
  );

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="w-64">
        <Label
          htmlFor={developerId}
          className="text-sm font-semibold text-gray-800"
        >
          {t("projects.developer")}
        </Label>
        <div className="mt-2">
          <Select
            disabled={isLoading || projectDevelopers.length === 0}
            value={selectedDeveloperId || "all"}
            onValueChange={handleDeveloperChange}
          >
            <SelectTrigger
              id={developerId}
              className="w-full bg-white border border-gray-300 h-11"
            >
              <SelectValue placeholder={t("projects.selectDeveloper")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("projects.allDevelopers")}</SelectItem>
              {projectDevelopers.map((developer) => (
                <SelectItem key={developer.id} value={developer.id}>
                  {developer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="w-48">
        <Label
          htmlFor={countryId}
          className="text-sm font-semibold text-gray-800"
        >
          {t("projects.country")}
        </Label>
        <div className="mt-2">
          <Select
            disabled={isLoadingCountries || countryList.length === 0}
            value={selectedCountryCode || "all"}
            onValueChange={handleCountryChange}
          >
            <SelectTrigger
              id={countryId}
              className="w-full bg-white border border-gray-300 h-11"
            >
              <SelectValue placeholder={t("projects.selectCountry")}>
                {selectedCountryCode &&
                  renderCountryOption(selectedCountryCode)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("projects.allCountries")}</SelectItem>
              {countryList.map((country) => (
                <SelectItem
                  key={country.countryCode}
                  value={country.countryCode}
                  className="flex items-center gap-2"
                >
                  {renderCountryOption(country.countryCode)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default ProjectFilter;
